<template>
  <div class="qr-page py-3">
    <header class="qr-toolbar">
      <div class="qr-toolbar__title">
        <span class="qr-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Quick Replies</h1>
          <p>
            <span class="qr-toolbar__org">{{ activeOrganisationName || 'the current organisation' }}</span>
            · Manage canned replies and product details used by tele-sales staff
          </p>
        </div>
      </div>
      <div class="qr-toolbar__actions">
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadData"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton
          color="primary"
          size="sm"
          class="create-btn"
          @click="openCreate"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="create-btn__icon" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>New Reply</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="qr-alert">{{ errorMessage }}</CAlert>

    <div class="qr-tabs" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        class="qr-tab"
        :class="{ 'qr-tab--active': activeCategory === tab.id }"
        :aria-selected="activeCategory === tab.id"
        @click="activeCategory = tab.id"
      >
        <span>{{ tab.label }}</span>
        <span class="qr-tab-count">{{ countFor(tab.id) }}</span>
      </button>
    </div>

    <BaseTable
      :items="filteredItems"
      :columns="columns"
      :title="activeTabLabel"
      accent="linear-gradient(180deg, #0e5b46, #16a07a)"
      :loading="isLoading"
      :default-sort="{ column: 'title', direction: 'asc' }"
      striped
      hover
      bordered
      sticky-header
      class="qr-table"
    >
      <template #toolbar>
        <div class="qr-toolbar-filters">
          <div v-if="isAdminTier" class="qr-dept-filter">
            <AppDropdown
              v-model="departmentFilter"
              :options="departmentFilterOptions"
              placeholder="All departments"
              searchable
              search-placeholder="Filter by department…"
            />
          </div>
          <div class="qr-search-wrap">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="qr-search-icon" aria-hidden="true">
              <circle cx="11" cy="11" r="7"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              v-model.trim="searchTerm"
              type="text"
              class="qr-input"
              placeholder="Search by title or text…"
            />
            <button
              v-if="searchTerm"
              type="button"
              class="qr-search-clear"
              aria-label="Clear search"
              @click="searchTerm = ''"
            >×</button>
          </div>
        </div>
      </template>

      <template #cell-title="{ row }">
        <span class="qr-title">{{ row.title }}</span>
      </template>

      <template #cell-text="{ row }">
        <span class="qr-text" :title="row.text">{{ row.text }}</span>
      </template>

      <template #cell-department="{ row }">
        <span v-if="departmentName(row.department_id)" class="qr-dept-pill">
          {{ departmentName(row.department_id) }}
        </span>
        <span v-else class="qr-dept-none">All</span>
      </template>

      <template #cell-actions="{ row }">
        <div class="qr-row-actions">
          <button type="button" class="qr-action-btn qr-action-btn--edit" @click="openEdit(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            <span>Edit</span>
          </button>
          <button type="button" class="qr-action-btn qr-action-btn--delete" @click="openDelete(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/>
              <path d="M14 11v6"/>
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
            </svg>
            <span>Delete</span>
          </button>
        </div>
      </template>

      <template #empty>
        <div v-if="!items.length" class="qr-empty">
          <div class="qr-empty__title">No {{ activeTabLabel.toLowerCase() }} yet</div>
          <div class="qr-empty__hint">
            <button type="button" class="qr-empty__link" @click="openCreate">Add the first one</button>
            for {{ activeOrganisationName || 'this organisation' }}.
          </div>
        </div>
        <div v-else class="qr-empty">
          <div class="qr-empty__title">No matches for "{{ searchTerm }}"</div>
          <div class="qr-empty__hint">
            Try a different keyword, or
            <button type="button" class="qr-empty__link" @click="searchTerm = ''">clear search</button>.
          </div>
        </div>
      </template>
    </BaseTable>

    <transition name="qr-fade">
      <div
        v-if="formOpen"
        class="qr-modal-backdrop"
        @click.self="closeForm"
      >
        <div class="qr-modal" role="dialog" aria-modal="true" :aria-label="formTitle">
          <header class="qr-modal__header">
            <div class="qr-modal__title-block">
              <span class="qr-modal__accent" aria-hidden="true"></span>
              <div>
                <h2 class="qr-modal__title">{{ formTitle }}</h2>
                <p class="qr-modal__subtitle">{{ formSubtitle }}</p>
              </div>
            </div>
            <button
              type="button"
              class="qr-modal__close"
              aria-label="Close"
              @click="closeForm"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </header>

          <form class="qr-form" @submit.prevent="saveForm">
            <div class="qr-form__row">
              <label class="qr-form__label">
                Category <span class="qr-form__required">*</span>
              </label>
              <div class="qr-form__segment" role="radiogroup" aria-label="Category">
                <button
                  v-for="opt in categoryOptions"
                  :key="opt.value"
                  type="button"
                  role="radio"
                  :aria-checked="form.category === opt.value"
                  class="qr-form__segment-btn"
                  :class="{ 'qr-form__segment-btn--active': form.category === opt.value }"
                  @click="form.category = opt.value"
                >{{ opt.label }}</button>
              </div>
            </div>

            <div class="qr-form__row">
              <label for="qr-form-title" class="qr-form__label">
                Title <span class="qr-form__required">*</span>
              </label>
              <input
                id="qr-form-title"
                ref="titleInput"
                v-model.trim="form.title"
                type="text"
                class="qr-form__input"
                placeholder="e.g. AGRO YIELD"
                maxlength="120"
                required
              />
              <p class="qr-form__hint">Short, scannable label — staff use this to find the reply.</p>
            </div>

            <div class="qr-form__row">
              <label for="qr-form-text" class="qr-form__label">
                Text <span class="qr-form__required">*</span>
              </label>
              <textarea
                id="qr-form-text"
                v-model="form.text"
                class="qr-form__textarea"
                placeholder="The message that gets copied to clipboard when staff click this reply."
                rows="7"
                required
              />
              <p class="qr-form__hint">
                Plain text. Line breaks and emojis are preserved.
                <span class="qr-form__counter">{{ form.text.length }} chars</span>
              </p>
            </div>

            <div class="qr-form__row">
              <label class="qr-form__label">Department</label>
              <AppDropdown
                v-model="form.department_id"
                :options="departmentOptions"
                placeholder="All departments"
                searchable
                search-placeholder="Search department…"
              />
              <p class="qr-form__hint">Leave empty to make this reply visible to all departments.</p>
            </div>

            <CAlert v-if="formError" color="danger" show class="qr-form__alert">{{ formError }}</CAlert>

            <footer class="qr-form__footer">
              <button type="button" class="qr-btn qr-btn--ghost" :disabled="isSaving" @click="closeForm">Cancel</button>
              <button type="submit" class="qr-btn qr-btn--primary" :disabled="!canSubmit || isSaving">
                {{ isSaving ? 'Saving…' : (isEditing ? 'Save changes' : 'Create reply') }}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </transition>

    <transition name="qr-fade">
      <div
        v-if="deleteTarget"
        class="qr-modal-backdrop"
        @click.self="closeDelete"
      >
        <div class="qr-modal qr-modal--confirm" role="alertdialog" aria-modal="true" aria-label="Confirm delete">
          <header class="qr-modal__header">
            <div class="qr-modal__title-block">
              <span class="qr-modal__accent qr-modal__accent--danger" aria-hidden="true"></span>
              <div>
                <h2 class="qr-modal__title">Delete reply?</h2>
                <p class="qr-modal__subtitle">This cannot be undone.</p>
              </div>
            </div>
            <button
              type="button"
              class="qr-modal__close"
              aria-label="Close"
              @click="closeDelete"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </header>

          <div class="qr-confirm-body">
            <p class="qr-confirm-body__lead">
              "<strong>{{ deleteTarget.title }}</strong>" will be removed permanently. Staff will no longer see this reply in the quick replies panel.
            </p>
          </div>

          <footer class="qr-form__footer">
            <button type="button" class="qr-btn qr-btn--ghost" :disabled="isDeleting" @click="closeDelete">Cancel</button>
            <button type="button" class="qr-btn qr-btn--danger" :disabled="isDeleting" @click="confirmDelete">
              {{ isDeleting ? 'Deleting…' : 'Delete reply' }}
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import BaseTable from '@/components/BaseTable.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import { getStoredRoles } from '@/auth/access'

const CATEGORY_CANNED = 'canned_reply'
const CATEGORY_PRODUCT = 'product_details'
const ADMIN_TIER_ROLES = ['superadmin', 'admin']
const DEPT_FILTER_ALL = ''
const DEPT_FILTER_ORG_WIDE = '__org_wide__'

const EMPTY_FORM = () => ({
  id: null,
  title: '',
  text: '',
  category: CATEGORY_CANNED,
  department_id: '',
})

export default {
  name: 'SettingsQuickReplies',
  components: { BaseTable, AppDropdown },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      searchTerm: '',
      departmentFilter: DEPT_FILTER_ALL,
      activeCategory: CATEGORY_CANNED,
      products: [],
      cannedReplies: [],
      departments: [],
      tabs: [
        { id: CATEGORY_CANNED, label: 'Canned Replies' },
        { id: CATEGORY_PRODUCT, label: 'Product Details' },
      ],
      categoryOptions: [
        { value: CATEGORY_CANNED, label: 'Canned Reply' },
        { value: CATEGORY_PRODUCT, label: 'Product Details' },
      ],
      columns: [
        { key: 'title', label: 'Title', width: '220px' },
        { key: 'text', label: 'Text' },
        { key: 'department', label: 'Department', width: '160px', sortable: false },
        { key: 'actions', label: '', width: '160px', align: 'right', sortable: false },
      ],
      formOpen: false,
      form: EMPTY_FORM(),
      formError: '',
      isSaving: false,
      deleteTarget: null,
      isDeleting: false,
    }
  },
  computed: {
    activeOrganisationName() {
      return this.$store?.state?.activeOrganisationName || ''
    },
    isAdminTier() {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      return roles.some((r) => ADMIN_TIER_ROLES.includes(r))
    },
    items() {
      return this.activeCategory === CATEGORY_PRODUCT ? this.products : this.cannedReplies
    },
    departmentScopedItems() {
      if (!this.isAdminTier || this.departmentFilter === DEPT_FILTER_ALL) return this.items
      if (this.departmentFilter === DEPT_FILTER_ORG_WIDE) {
        return this.items.filter((item) => item.department_id === null || item.department_id === undefined || item.department_id === '')
      }
      return this.items.filter((item) => String(item.department_id) === String(this.departmentFilter))
    },
    filteredItems() {
      const term = this.searchTerm.toLowerCase()
      if (!term) return this.departmentScopedItems
      return this.departmentScopedItems.filter((item) => {
        const title = (item.title || '').toLowerCase()
        const text = (item.text || '').toLowerCase()
        return title.includes(term) || text.includes(term)
      })
    },
    departmentFilterOptions() {
      const opts = this.departments.map((dept) => ({
        value: dept.id,
        label: dept.name,
      }))
      return [
        { value: DEPT_FILTER_ALL, label: 'All departments' },
        { value: DEPT_FILTER_ORG_WIDE, label: 'Org-wide (no department)' },
        ...opts,
      ]
    },
    activeTabLabel() {
      const tab = this.tabs.find((t) => t.id === this.activeCategory)
      return tab ? tab.label : ''
    },
    departmentOptions() {
      const opts = this.departments.map((dept) => ({
        value: dept.id,
        label: dept.name,
      }))
      return [{ value: '', label: 'All departments' }, ...opts]
    },
    isEditing() {
      return this.form.id !== null && this.form.id !== undefined
    },
    formTitle() {
      return this.isEditing ? 'Edit reply' : 'New reply'
    },
    formSubtitle() {
      return this.isEditing
        ? 'Update the title, text, or category — changes are immediate.'
        : 'Add a reply for staff to copy from the quick replies panel.'
    },
    canSubmit() {
      return this.form.title.trim().length > 0 && this.form.text.trim().length > 0 && !!this.form.category
    },
  },
  watch: {
    activeCategory() {
      this.searchTerm = ''
    },
  },
  created() {
    this.loadData()
  },
  methods: {
    countFor(category) {
      return category === CATEGORY_PRODUCT ? this.products.length : this.cannedReplies.length
    },
    departmentName(id) {
      if (id === null || id === undefined || id === '') return ''
      const match = this.departments.find((dept) => String(dept.id) === String(id))
      return match ? match.name : ''
    },
    loadData() {
      this.isLoading = true
      this.errorMessage = ''

      const repliesPromise = axios.get(`${this.$apiAdress}/api/canned_replies`)
      const departmentsPromise = axios.get(`${this.$apiAdress}/api/department`)

      Promise.all([repliesPromise, departmentsPromise])
        .then(([repliesRes, deptsRes]) => {
          const repliesData = repliesRes.data || {}
          this.products = Array.isArray(repliesData.products) ? repliesData.products : []
          this.cannedReplies = Array.isArray(repliesData.canned_replies) ? repliesData.canned_replies : []
          this.departments = this.extractDepartments(deptsRes.data)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load quick replies right now.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    extractDepartments(data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.data)) return data.data
      if (Array.isArray(data?.departments)) return data.departments
      return []
    },
    openCreate() {
      this.form = EMPTY_FORM()
      this.form.category = this.activeCategory
      this.formError = ''
      this.formOpen = true
      this.$nextTick(() => {
        if (this.$refs.titleInput) this.$refs.titleInput.focus()
      })
    },
    openEdit(row) {
      this.form = {
        id: row.id,
        title: row.title || '',
        text: row.text || '',
        category: row.category || CATEGORY_CANNED,
        department_id: row.department_id !== null && row.department_id !== undefined ? row.department_id : '',
      }
      this.formError = ''
      this.formOpen = true
      this.$nextTick(() => {
        if (this.$refs.titleInput) this.$refs.titleInput.focus()
      })
    },
    closeForm() {
      if (this.isSaving) return
      this.formOpen = false
      this.formError = ''
    },
    async saveForm() {
      if (!this.canSubmit || this.isSaving) return
      this.isSaving = true
      this.formError = ''

      const payload = {
        title: this.form.title.trim(),
        text: this.form.text,
        category: this.form.category,
        department_id: this.form.department_id === '' ? null : this.form.department_id,
      }

      try {
        if (this.isEditing) {
          payload.id = this.form.id
          await axios.put(`${this.$apiAdress}/api/canned_replies/${this.form.id}`, payload)
          this.$toast.success('Reply updated')
        } else {
          await axios.post(`${this.$apiAdress}/api/canned_replies`, payload)
          this.$toast.success('Reply created')
        }
        this.activeCategory = payload.category
        this.formOpen = false
        await this.refreshSilently()
      } catch (error) {
        const message = error?.response?.data?.error
        if (message && typeof message === 'object') {
          this.formError = Object.values(message).flat().join(' ')
        } else if (typeof message === 'string') {
          this.formError = message
        } else {
          this.formError = 'Save failed. Please try again.'
        }
      } finally {
        this.isSaving = false
      }
    },
    openDelete(row) {
      this.deleteTarget = row
    },
    closeDelete() {
      if (this.isDeleting) return
      this.deleteTarget = null
    },
    async confirmDelete() {
      if (!this.deleteTarget || this.isDeleting) return
      this.isDeleting = true
      try {
        await axios.delete(`${this.$apiAdress}/api/canned_replies/${this.deleteTarget.id}`)
        this.$toast.success('Reply deleted')
        this.deleteTarget = null
        await this.refreshSilently()
      } catch (error) {
        const message = error?.response?.data?.error || 'Delete failed. Please try again.'
        this.$toast.error(typeof message === 'string' ? message : 'Delete failed.')
      } finally {
        this.isDeleting = false
      }
    },
    async refreshSilently() {
      try {
        const response = await axios.get(`${this.$apiAdress}/api/canned_replies`)
        const data = response.data || {}
        this.products = Array.isArray(data.products) ? data.products : []
        this.cannedReplies = Array.isArray(data.canned_replies) ? data.canned_replies : []
      } catch (error) {
        // Ignore — toast on the action already conveyed success.
      }
    },
  },
}
</script>

<style scoped>
.qr-page {
  padding: 0.25rem 0;
}

.qr-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.qr-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1 1 auto;
  min-width: 0;
}

.qr-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.qr-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.qr-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.qr-toolbar__org {
  color: #0e5b46;
  font-weight: 700;
}

.qr-toolbar__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-left: auto;
  flex-shrink: 0;
}

.qr-toolbar__actions >>> .btn.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.qr-toolbar__actions >>> .btn.create-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.qr-toolbar__actions >>> .btn.create-btn:active {
  transform: translateY(0);
  filter: brightness(0.96);
}

.create-btn__icon {
  flex-shrink: 0;
  display: block;
}

.qr-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.qr-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.qr-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.qr-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.qr-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: qr-refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes qr-refresh-spin {
  to { transform: rotate(360deg); }
}

.qr-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.qr-tabs {
  display: flex;
  gap: 0.4rem;
  margin: 0 0 0.9rem;
  padding: 0.35rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  width: fit-content;
}

.qr-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.9rem;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}

.qr-tab:hover {
  background: #f5fbf8;
  color: #0e5b46;
}

.qr-tab--active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
}

.qr-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 0 6px;
  height: 18px;
  border-radius: 999px;
  background: #eef1f7;
  color: #4b5563;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.qr-tab--active .qr-tab-count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.qr-table {
  margin-bottom: 1rem;
}

.qr-toolbar-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.qr-dept-filter {
  width: 220px;
  max-width: 100%;
}

.qr-search-wrap {
  position: relative;
  width: 280px;
  max-width: 100%;
}

.qr-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.qr-input {
  width: 100%;
  height: 34px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.qr-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.qr-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.qr-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

.qr-title {
  font-size: 0.86rem;
  font-weight: 600;
  color: #1f2a44;
  word-break: break-word;
}

.qr-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 0.78rem;
  color: #4b5563;
  line-height: 1.4;
  max-width: 560px;
  word-break: break-word;
}

.qr-dept-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  background: #ecfdf5;
  color: #0e5b46;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  border: 1px solid #c7ebda;
}

.qr-dept-none {
  font-size: 0.7rem;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.qr-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: flex-end;
}

.qr-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.qr-action-btn--edit:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}

.qr-action-btn--delete:hover {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.12);
}

.qr-empty {
  text-align: center;
  padding: 1.5rem 1rem;
}

.qr-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.qr-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.qr-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.qr-empty__link:hover {
  text-decoration: underline;
}

.qr-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
}

.qr-modal {
  width: 560px;
  max-width: 100%;
  max-height: calc(100vh - 2.5rem);
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qr-modal--confirm {
  width: 460px;
}

.qr-modal__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.1rem 0.9rem;
  border-bottom: 1px solid #e5e9f2;
  background: #fbfcfe;
}

.qr-modal__header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.qr-modal--confirm .qr-modal__header::before {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
}

.qr-modal__title-block {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  min-width: 0;
}

.qr-modal__accent {
  margin-top: 4px;
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  flex: 0 0 auto;
}

.qr-modal__accent--danger {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
}

.qr-modal__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.qr-modal__subtitle {
  margin: 2px 0 0;
  font-size: 0.74rem;
  color: #6b7280;
  line-height: 1.3;
}

.qr-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.qr-modal__close:hover {
  background: #f3f6fb;
  border-color: #c7cfdb;
  color: #1f2a44;
}

.qr-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 1.1rem 1.1rem 0;
  overflow-y: auto;
}

.qr-form__row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.qr-form__label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.qr-form__required {
  color: #ef4444;
  margin-left: 2px;
}

.qr-form__input,
.qr-form__textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.86rem;
  color: #1f2a44;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.qr-form__input {
  height: 38px;
}

.qr-form__textarea {
  resize: vertical;
  min-height: 140px;
  line-height: 1.45;
}

.qr-form__input:focus,
.qr-form__textarea:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.qr-form__hint {
  margin: 0;
  font-size: 0.72rem;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.qr-form__counter {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #4b5563;
}

.qr-form__segment {
  display: inline-flex;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  padding: 3px;
  background: #f7f9fc;
  width: fit-content;
}

.qr-form__segment-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}

.qr-form__segment-btn:hover:not(.qr-form__segment-btn--active) {
  color: #0e5b46;
}

.qr-form__segment-btn--active {
  background: #ffffff;
  color: #0e5b46;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.qr-form__alert {
  margin: 0;
  border-radius: 8px;
  font-size: 0.78rem;
}

.qr-form__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.9rem 1.1rem 1.1rem;
  margin-top: 0.4rem;
  border-top: 1px solid #eef1f7;
  background: #fbfcfe;
}

.qr-confirm-body {
  padding: 1rem 1.1rem 0;
}

.qr-confirm-body__lead {
  margin: 0;
  font-size: 0.86rem;
  color: #1f2a44;
  line-height: 1.5;
}

.qr-confirm-body__lead strong {
  color: #0e5b46;
}

.qr-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease, transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease;
  border: 1px solid transparent;
}

.qr-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.qr-btn--ghost {
  background: #ffffff;
  border-color: #d8dde6;
  color: #1f2a44;
}

.qr-btn--ghost:hover:not(:disabled) {
  background: #f3f6fb;
  border-color: #c7cfdb;
}

.qr-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.qr-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.qr-btn--danger {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(185, 28, 28, 0.2);
}

.qr-btn--danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(185, 28, 28, 0.28);
  filter: brightness(1.05);
}

.qr-fade-enter-active,
.qr-fade-leave-active {
  transition: opacity 0.18s ease;
}

.qr-fade-enter,
.qr-fade-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .qr-toolbar {
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .qr-toolbar__title {
    flex: 1 1 100%;
  }
  .qr-toolbar__actions {
    flex: 1 1 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
  .qr-search-wrap {
    width: 100%;
  }
  .qr-modal {
    width: 100%;
  }
}

@media (max-width: 575px) {
  .qr-page {
    padding: 0 0 0.5rem;
  }
  .qr-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .qr-toolbar__accent {
    height: 30px;
  }
  .qr-toolbar__title h1 {
    font-size: 1rem;
  }
  .qr-toolbar__title p {
    font-size: 0.72rem;
  }
  .qr-toolbar__actions {
    gap: 0.4rem;
  }
  .qr-toolbar__actions >>> .btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .qr-modal-backdrop {
    padding: 0.5rem;
    align-items: flex-end;
  }
  .qr-modal {
    max-height: calc(100vh - 1rem);
    border-radius: 12px;
  }
  .qr-modal__header {
    padding: 0.85rem 0.85rem 0.75rem;
  }
  .qr-row-actions {
    flex-wrap: wrap;
    gap: 0.3rem;
  }
}
</style>
